import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
    FaVideo,
    FaRegCalendarAlt,
    FaLink,
    FaUsers,
    FaPlayCircle,
    FaStopCircle,
    FaPlus,
    FaCloudUploadAlt,
    FaTimes,
    FaBook,
    FaFileVideo,
    FaChevronRight,
    FaClipboard,
    FaEdit
} from 'react-icons/fa';
import DataTable from '../../SuperAdmin/shared/DataTable';
import Loader from '../../shared/Loader';
import { showSuccess, showError, showLoading, closeLoading } from '../../../utils/Alert';
import './TutorDashboardOverview.css';
import '../../SuperAdmin/views/UserModals.css';

const TutorLiveClasses = () => {
    const [classes, setClasses] = useState([]);
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isRecordingModalOpen, setIsRecordingModalOpen] = useState(false);
    const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false);
    const [currentClass, setCurrentClass] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    const [formData, setFormData] = useState({
        courseId: '',
        moduleId: '',
        topic: '',
        scheduledDate: '',
        startTime: '',
        duration: '60',
        externalMeetingUrl: ''
    });

    const [materialData, setMaterialData] = useState({ title: '', url: '' });
    const [recordingData, setRecordingData] = useState({ recordedVideoUrl: '', recordingFile: null });

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const [classesRes, coursesRes] = await Promise.all([
                axios.get('/api/tutor/live-classes', { headers: { Authorization: `Bearer ${token}` } }),
                axios.get('/api/tutor/courses', { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setClasses(classesRes.data);
            setCourses(coursesRes.data.filter(c => c.status === 'Active' || c.status === 'Pending'));
            setLoading(false);
        } catch (err) {
            console.error("Error fetching data", err);
            showError("Load Failed", "Could not fetch essential data.");
            setLoading(false);
        }
    };

    const handleFormChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleScheduleSubmit = async (e) => {
        e.preventDefault();
        showLoading('Publishing Live Session...');
        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/tutor/live-classes', formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            showSuccess('Session Scheduled', `The live class "${formData.topic}" is now officially on the calendar.`);
            setIsModalOpen(false);
            setFormData({ courseId: '', moduleId: '', topic: '', scheduledDate: '', startTime: '', duration: '60', externalMeetingUrl: '' });
            fetchInitialData();
        } catch (err) {
            console.error("Error scheduling class", err);
            showError("Schedule Failed", err.response?.data?.message || "Check your details.");
            closeLoading();
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        showLoading('Updating Session Details...');
        try {
            const token = localStorage.getItem('token');
            await axios.patch(`/api/tutor/live-classes/${currentClass._id}`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            showSuccess('Session Updated', `The details for "${formData.topic}" have been successfully revised.`);
            setIsEditModalOpen(false);
            setCurrentClass(null);
            setFormData({ courseId: '', moduleId: '', topic: '', scheduledDate: '', startTime: '', duration: '60', externalMeetingUrl: '' });
            fetchInitialData();
        } catch (err) {
            console.error("Error updating class", err);
            showError("Update Failed", err.response?.data?.message || "Check your details.");
            closeLoading();
        }
    };

    const toggleClassStatus = async (cls, targetStatus) => {
        showLoading(`${targetStatus === 'Live' ? 'Starting' : 'Ending'} Session...`);
        try {
            const token = localStorage.getItem('token');
            await axios.patch(`/api/tutor/live-classes/${cls._id}/status`, { status: targetStatus }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            showSuccess(`Session ${targetStatus}`, `The class state has been updated successfully.`);
            fetchInitialData();
        } catch (err) {
            showError("Status Update Failed", "Could not change class state.");
            closeLoading();
        }
    };

    const handleAddMaterial = async (e) => {
        e.preventDefault();
        if (!materialData.title || !materialData.url) return;
        showLoading('Adding Resource...');
        try {
            const token = localStorage.getItem('token');
            await axios.patch(`/api/tutor/live-classes/${currentClass._id}/materials`, materialData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            showSuccess('Material Added', 'Students can now see this resource link.');
            setMaterialData({ title: '', url: '' });
            setIsMaterialModalOpen(false);
            fetchInitialData();
        } catch (err) {
            showError("Update Failed", "Could not add material link.");
            closeLoading();
        }
    };

    const handleArchiveRecording = async (e) => {
        e.preventDefault();
        showLoading('Archiving Recording...');
        const data = new FormData();
        if (recordingData.recordingFile) {
            data.append('recordingFile', recordingData.recordingFile);
        } else if (recordingData.recordedVideoUrl) {
            data.append('recordedVideoUrl', recordingData.recordedVideoUrl);
        } else {
            showError("Missing Data", "Please provide a file or a link.");
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await axios.patch(`/api/tutor/live-classes/${currentClass._id}/recording`, data, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            showSuccess('Archive Complete', 'The recording is now available for all students.');
            setRecordingData({ recordedVideoUrl: '', recordingFile: null });
            setIsRecordingModalOpen(false);
            fetchInitialData();
        } catch (err) {
            showError("Archive Failed", "Could not upload recording.");
            closeLoading();
        }
    };

    const classColumns = [
        {
            header: 'Topic & Course', key: 'topic', width: '25%', render: (row) => (
                <div>
                    <strong style={{ color: '#0F172A' }}>{row.topic}</strong>
                    <div style={{ fontSize: '11px', color: '#64748B', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FaBook style={{ fontSize: '10px' }} /> {row.courseId?.title || 'Unknown Course'}
                    </div>
                </div>
            )
        },
        {
            header: 'Schedule', key: 'datetime', width: '20%', render: (row) => (
                <div>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                        <FaRegCalendarAlt style={{ color: 'var(--primary-blue)' }} />
                        {new Date(row.scheduledDate).toLocaleDateString()}
                    </span>
                    <div style={{ fontSize: '12px', color: '#64748B' }}>{row.startTime} ({row.duration} Min)</div>
                </div>
            )
        },
        {
            header: 'Attendance', key: 'attendance', width: '15%', render: (row) => (
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FaUsers style={{ color: '#3B82F6' }} /> {row.attendance?.length || 0} Joined
                </span>
            )
        },
        {
            header: 'Status', key: 'status', width: '15%', render: (row) => (
                <span className={`status-badge ${row.status.toLowerCase()}`}>
                    {row.status === 'Live' && <span className="live-indicator-dot"></span>}
                    {row.status}
                </span>
            )
        },
        {
            header: 'Actions / Archive', key: 'actions', width: '25%', render: (row) => (
                <div className="action-buttons">
                    {row.status === 'Scheduled' && (
                        <>
                            <button className="action-btn view" title="Start Session" onClick={() => toggleClassStatus(row, 'Live')}>
                                <FaPlayCircle style={{ color: '#10B981' }} />
                            </button>
                            <button className="action-btn edit" title="Edit Session" onClick={() => {
                                setCurrentClass(row);
                                setFormData({
                                    courseId: row.courseId?._id,
                                    moduleId: row.moduleId,
                                    topic: row.topic,
                                    scheduledDate: row.scheduledDate.split('T')[0],
                                    startTime: row.startTime,
                                    duration: row.duration,
                                    externalMeetingUrl: row.externalMeetingUrl
                                });
                                setIsEditModalOpen(true);
                            }}>
                                <FaEdit />
                            </button>
                            <button className="action-btn edit" title="Copy Link" onClick={() => {
                                navigator.clipboard.writeText(row.externalMeetingUrl);
                                showSuccess('Copied', 'External meeting link copied.');
                            }}>
                                <FaLink />
                            </button>
                        </>
                    )}
                    {row.status === 'Live' && (
                        <>
                            <button className="action-btn delete" title="End Session" onClick={() => toggleClassStatus(row, 'Completed')}>
                                <FaStopCircle style={{ color: '#E11D48' }} />
                            </button>
                            <button className="action-btn edit" title="Edit Session" onClick={() => {
                                setCurrentClass(row);
                                setFormData({
                                    courseId: row.courseId?._id,
                                    moduleId: row.moduleId,
                                    topic: row.topic,
                                    scheduledDate: row.scheduledDate.split('T')[0],
                                    startTime: row.startTime,
                                    duration: row.duration,
                                    externalMeetingUrl: row.externalMeetingUrl
                                });
                                setIsEditModalOpen(true);
                            }}>
                                <FaEdit />
                            </button>
                            <button className="action-btn-gold" onClick={() => { setCurrentClass(row); setIsMaterialModalOpen(true); }} style={{ padding: '6px 10px', fontSize: '12px' }}>
                                Share Link
                            </button>
                        </>
                    )}
                    {row.status === 'Completed' && (
                        <button className="action-btn-gold" style={{ padding: '6px 12px', fontSize: '12px', gap: '8px' }} onClick={() => { setCurrentClass(row); setIsRecordingModalOpen(true); }}>
                            {row.recordedVideoUrl ? <FaClipboard /> : <FaCloudUploadAlt />}
                            {row.recordedVideoUrl ? 'View Recording' : 'Archive Video'}
                        </button>
                    )}
                </div>
            )
        }
    ];

    if (loading) return <Loader />;

    return (
        <div className="dashboard-overview fade-in">
            <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <div>
                    <h1 className="page-title">Live Class Operations</h1>
                    <p className="page-subtitle">Manage virtual sessions, distribute resources in real-time, and archive recordings.</p>
                </div>
                <button className="primary-btn pulse-on-hover" onClick={() => setIsModalOpen(true)}>
                    <FaPlus style={{ marginRight: '8px' }} /> Schedule Class
                </button>
            </div>

            <DataTable
                title="Your Academic Schedule"
                columns={classColumns}
                data={classes}
                filterOptions={[
                    { label: 'All Sessions', value: '' },
                    { label: 'Live Now', value: 'Live' },
                    { label: 'Scheduled', value: 'Scheduled' },
                    { label: 'Completed', value: 'Completed' }
                ]}
            />

            {/* Schedule Modal */}
            {isModalOpen && createPortal(
                <div className={`modal-overlay ${isModalOpen ? 'open' : ''}`} onClick={() => setIsModalOpen(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '800px' }}>
                        <div className="modal-header">
                            <h2><FaVideo /> Provision Live Session</h2>
                            <button className="modal-close-btn" onClick={() => setIsModalOpen(false)}><FaTimes /></button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleScheduleSubmit} className="modal-form">
                                <div className="form-row" style={{ gridColumn: 'span 2' }}>
                                    <div className="form-group">
                                        <label className="premium-label">Select Course</label>
                                        <select name="courseId" value={formData.courseId} onChange={handleFormChange} className="glass-input" required>
                                            <option value="">-- Choose Course --</option>
                                            {courses.map(c => <option key={c._id} value={c._id}>{c.title}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label className="premium-label">Select Module</label>
                                        <select name="moduleId" value={formData.moduleId} onChange={handleFormChange} className="glass-input" required disabled={!formData.courseId}>
                                            <option value="">-- Choose Module --</option>
                                            {courses.find(c => c._id === formData.courseId)?.modules.map((m, idx) => (
                                                <option key={m._id} value={m._id}>Mod {idx + 1}: {m.moduleTitle}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                    <label className="premium-label">Lecture Topic</label>
                                    <input type="text" name="topic" value={formData.topic} onChange={handleFormChange} className="glass-input" required placeholder="e.g. Advanced State Management" />
                                </div>
                                <div className="form-row triplet" style={{ gridColumn: 'span 2' }}>
                                    <div className="form-group">
                                        <label className="premium-label">Date</label>
                                        <input type="date" name="scheduledDate" value={formData.scheduledDate} onChange={handleFormChange} className="glass-input" required />
                                    </div>
                                    <div className="form-group">
                                        <label className="premium-label">Start Time</label>
                                        <input type="time" name="startTime" value={formData.startTime} onChange={handleFormChange} className="glass-input" required />
                                    </div>
                                    <div className="form-group">
                                        <label className="premium-label">Duration (Min)</label>
                                        <input type="number" name="duration" value={formData.duration} onChange={handleFormChange} className="glass-input" required step="15" min="15" />
                                    </div>
                                </div>
                                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                    <label className="premium-label"><FaLink /> External Meeting URL (Google Meet / Zoom)</label>
                                    <input type="url" name="externalMeetingUrl" value={formData.externalMeetingUrl} onChange={handleFormChange} className="glass-input" required placeholder="https://meet.google.com/..." />
                                </div>
                                <div className="modal-actions">
                                    <button type="button" className="action-btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
                                    <button type="submit" className="action-btn-gold pulse-on-hover">Publish Schedule <FaChevronRight style={{ marginLeft: '10px' }} /></button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Material Modal */}
            {isMaterialModalOpen && createPortal(
                <div className={`modal-overlay ${isMaterialModalOpen ? 'open' : ''}`} onClick={() => setIsMaterialModalOpen(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                        <div className="modal-header">
                            <h2><FaLink /> Share Resource</h2>
                            <button className="modal-close-btn" onClick={() => setIsMaterialModalOpen(false)}><FaTimes /></button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleAddMaterial} className="modal-form">
                                <div className="form-group">
                                    <label className="premium-label">Resource Title</label>
                                    <input type="text" value={materialData.title} onChange={e => setMaterialData({ ...materialData, title: e.target.value })} className="glass-input" placeholder="e.g. GitHub Repository link" required />
                                </div>
                                <div className="form-group">
                                    <label className="premium-label">Resource URL</label>
                                    <input type="url" value={materialData.url} onChange={e => setMaterialData({ ...materialData, url: e.target.value })} className="glass-input" placeholder="https://..." required />
                                </div>
                                <div className="modal-actions">
                                    <button type="button" className="action-btn-secondary" onClick={() => setIsMaterialModalOpen(false)}>Cancel</button>
                                    <button type="submit" className="action-btn-gold">Share with Students</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Recording Modal */}
            {isRecordingModalOpen && createPortal(
                <div className={`modal-overlay ${isRecordingModalOpen ? 'open' : ''}`} onClick={() => setIsRecordingModalOpen(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <div className="modal-header">
                            <h2><FaFileVideo /> Session Archive</h2>
                            <button className="modal-close-btn" onClick={() => setIsRecordingModalOpen(false)}><FaTimes /></button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleArchiveRecording} className="modal-form">
                                {currentClass?.recordedVideoUrl && (
                                    <div style={{ background: '#F8FAFC', padding: '15px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #E2E8F0' }}>
                                        <p style={{ margin: 0, fontSize: '13px', color: '#64748B' }}>
                                            <strong>Current Recording:</strong> <span style={{ wordBreak: 'break-all' }}>{currentClass.recordedVideoUrl}</span>
                                        </p>
                                    </div>
                                )}
                                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                    <label className="premium-label">Option 1: Paste Google Drive / External Link</label>
                                    <input type="url" value={recordingData.recordedVideoUrl} onChange={e => setRecordingData({ ...recordingData, recordedVideoUrl: e.target.value, recordingFile: null })} className="glass-input" placeholder="https://drive.google.com/..." />
                                </div>
                                <div style={{ textAlign: 'center', margin: '20px 0', position: 'relative', gridColumn: 'span 2' }}>
                                    <hr style={{ border: 'none', borderTop: '1px solid #E2E8F0' }} />
                                    <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'white', padding: '0 10px', color: '#94A3B8', fontSize: '12px' }}>OR</span>
                                </div>
                                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                    <label className="premium-label">Option 2: Upload Recording File (.mp4 / .mov)</label>
                                    <input type="file" onChange={e => setRecordingData({ ...recordingData, recordingFile: e.target.files[0], recordedVideoUrl: '' })} className="glass-input" accept="video/*" style={{ background: 'white' }} />
                                    <small style={{ color: '#64748B' }}>Max size: 1GB</small>
                                </div>
                                <div className="modal-actions">
                                    <button type="button" className="action-btn-secondary" onClick={() => setIsRecordingModalOpen(false)}>Cancel</button>
                                    <button type="submit" className="action-btn-gold">{currentClass?.recordedVideoUrl ? 'Update Archive' : 'Deploy Archive'}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Edit Modal */}
            {isEditModalOpen && createPortal(
                <div className={`modal-overlay ${isEditModalOpen ? 'open' : ''}`} onClick={() => { setIsEditModalOpen(false); setCurrentClass(null); }}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '800px' }}>
                        <div className="modal-header">
                            <h2><FaEdit /> Edit Session Details</h2>
                            <button className="modal-close-btn" onClick={() => { setIsEditModalOpen(false); setCurrentClass(null); }}><FaTimes /></button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleEditSubmit} className="modal-form">
                                <div className="form-row" style={{ gridColumn: 'span 2' }}>
                                    <div className="form-group">
                                        <label className="premium-label">Select Course</label>
                                        <select name="courseId" value={formData.courseId} onChange={handleFormChange} className="glass-input" required>
                                            <option value="">-- Choose Course --</option>
                                            {courses.map(c => <option key={c._id} value={c._id}>{c.title}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label className="premium-label">Select Module</label>
                                        <select name="moduleId" value={formData.moduleId} onChange={handleFormChange} className="glass-input" required disabled={!formData.courseId}>
                                            <option value="">-- Choose Module --</option>
                                            {courses.find(c => c._id === formData.courseId)?.modules.map((m, idx) => (
                                                <option key={m._id} value={m._id}>Mod {idx + 1}: {m.moduleTitle}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                    <label className="premium-label">Lecture Topic</label>
                                    <input type="text" name="topic" value={formData.topic} onChange={handleFormChange} className="glass-input" required placeholder="e.g. Advanced State Management" />
                                </div>
                                <div className="form-row triplet" style={{ gridColumn: 'span 2' }}>
                                    <div className="form-group">
                                        <label className="premium-label">Date</label>
                                        <input type="date" name="scheduledDate" value={formData.scheduledDate} onChange={handleFormChange} className="glass-input" required />
                                    </div>
                                    <div className="form-group">
                                        <label className="premium-label">Start Time</label>
                                        <input type="time" name="startTime" value={formData.startTime} onChange={handleFormChange} className="glass-input" required />
                                    </div>
                                    <div className="form-group">
                                        <label className="premium-label">Duration (Min)</label>
                                        <input type="number" name="duration" value={formData.duration} onChange={handleFormChange} className="glass-input" required step="15" min="15" />
                                    </div>
                                </div>
                                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                    <label className="premium-label"><FaLink /> External Meeting URL (Google Meet / Zoom)</label>
                                    <input type="url" name="externalMeetingUrl" value={formData.externalMeetingUrl} onChange={handleFormChange} className="glass-input" required placeholder="https://meet.google.com/..." />
                                </div>
                                <div className="modal-actions">
                                    <button type="button" className="action-btn-secondary" onClick={() => { setIsEditModalOpen(false); setCurrentClass(null); }}>Cancel</button>
                                    <button type="submit" className="action-btn-gold">Save Changes <FaChevronRight style={{ marginLeft: '10px' }} /></button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default TutorLiveClasses;
